import {observer} from 'mobx-react';
import React from 'react';
import {PageHeading, PromiseMessage, PromiseRetry, Redirect} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {UserEditForm} from '../edit-form/UserEditForm';
import {UserEditSceneStore} from './UserEditSceneStore';

function* renderer(store: UserEditSceneStore): ReactNodeGenerator {
  if (store.redirectTo !== null) {
    yield (<Redirect to={store.redirectTo} key="redirect"/>);
    return;
  }

  const userState = store.userState;

  if (userState?.isResolved === true)
    yield (<PageHeading key="heading"> Edit User {userState.value.Email} </PageHeading>);
  else
    yield (<PageHeading key="heading"> Edit User {store.userId} </PageHeading>);

  yield (<PromiseMessage key="user-loader"
                         label="Loading user" state={userState} align="center"/>);

  yield (<PromiseRetry key="user-retry"
                       state={userState} retry={store.loadUser} align="center"/>);

  const lookupState = store.lookupState;
  yield (<PromiseMessage key="lookup-loader"
                         label="Loading levels" state={lookupState} align="center"/>);

  yield (<PromiseRetry key="lookup-retry"
                       state={lookupState} retry={store.loadLookup} align="center"/>);

  const timeZonesState = store.timeZonesState;
  yield (<PromiseMessage key="timezone-loader"
                         label="Loading timezones" state={timeZonesState} align="center"/>);

  yield (<PromiseRetry key="timezone-retry"
                       state={timeZonesState} retry={store.loadTimeZones} align="center"/>);

  if (userState?.isResolved !== true ||
    lookupState?.isResolved !== true ||
    timeZonesState?.isResolved !== true)
    return;

  yield (
    <UserEditForm key="user-edit-form"
                  store={store.form!}/>
  );

  const saveState = store.saveState;
  const isSavePending = saveState?.isPending === true;
  yield (
    <div className="row" key="save-actions">
      <div className="col-auto ml-auto">
        <button type="button" className="btn btn-light"
                onClick={store.cancel}
                disabled={isSavePending}> Cancel
        </button>
        <button type="button" className="btn btn-primary"
                onClick={store.save}
                disabled={isSavePending}> Save
        </button>
      </div>
    </div>
  );

  yield (<PromiseMessage key="save-error"
                         label="Saving" state={saveState} align="right"/>);
}

interface Props {
  id: number;
}

@observer
export class UserEditScene extends React.Component<Props> {
  readonly store: UserEditSceneStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new UserEditSceneStore(props.id);
  }

  componentDidMount() {
    this.store.loadLookup();
    this.store.loadTimeZones();
    this.store.loadUser();
  }

  render() {
    return Array.from(renderer(this.store));
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}

